/**
 * Interval — distance between two pitches, with quality and quantity.
 *
 * An interval has a NUMBER (unison, second, third, ..., octave, ninth, ...)
 * and a QUALITY (perfect, major, minor, augmented, diminished). The combination
 * is what musicians actually hear and discuss.
 *
 * The engine represents intervals as (number, semitones) and computes quality
 * on demand. This avoids the awkwardness of separate fields drifting out of sync.
 */

import {
  type Pitch,
  letterIndex,
  toMidi,
  transposeByLetterAndSemitones,
} from "./pitch.js";

export type IntervalQuality =
  | "P"  // perfect
  | "M"  // major
  | "m"  // minor
  | "A"  // augmented
  | "d"; // diminished

export interface Interval {
  /** 1 = unison, 2 = second, 3 = third, ..., 8 = octave, 9 = ninth, ... */
  number: number;
  /** Total semitones, positive for ascending. */
  semitones: number;
}

/**
 * Build an interval from quality and number.
 * Throws if the combination doesn't exist (e.g. "perfect third").
 */
export function interval(quality: IntervalQuality, number: number): Interval {
  if (number < 1) throw new Error(`Interval number must be >= 1, got ${number}`);
  // Reduce to within an octave to compute base semitones, then add octave count.
  const simple = ((number - 1) % 7) + 1;
  const octaves = Math.floor((number - 1) / 7);
  const baseSemis = baseSemitones(quality, simple);
  if (baseSemis === null) {
    throw new Error(`Invalid interval: ${quality}${number}`);
  }
  return { number, semitones: baseSemis + octaves * 12 };
}

/** Semitone count for a simple (within-octave) interval. */
function baseSemitones(quality: IntervalQuality, simpleNumber: number): number | null {
  // Perfect-class intervals: 1, 4, 5, 8
  // Major/minor-class intervals: 2, 3, 6, 7
  const isPerfectClass = [1, 4, 5, 8].includes(simpleNumber);
  const naturalMajor: Record<number, number> = { 1: 0, 2: 2, 3: 4, 4: 5, 5: 7, 6: 9, 7: 11, 8: 12 };
  const natural = naturalMajor[simpleNumber];
  if (natural === undefined) return null;

  if (isPerfectClass) {
    switch (quality) {
      case "P": return natural;
      case "A": return natural + 1;
      case "d": return natural - 1;
      default: return null; // P-class can't be major or minor
    }
  } else {
    switch (quality) {
      case "M": return natural;
      case "m": return natural - 1;
      case "A": return natural + 1;
      case "d": return natural - 2;
      default: return null; // M-class can't be perfect
    }
  }
}

/** Compute interval quality from number and semitones. Inverse of baseSemitones. */
export function intervalQuality(iv: Interval): IntervalQuality {
  const simple = ((iv.number - 1) % 7) + 1;
  const octaves = Math.floor((iv.number - 1) / 7);
  const simpleSemis = iv.semitones - octaves * 12;
  const isPerfectClass = [1, 4, 5, 8].includes(simple);
  const naturalMajor: Record<number, number> = { 1: 0, 2: 2, 3: 4, 4: 5, 5: 7, 6: 9, 7: 11, 8: 12 };
  const natural = naturalMajor[simple]!;
  const diff = simpleSemis - natural;
  if (isPerfectClass) {
    if (diff === 0) return "P";
    if (diff === 1) return "A";
    if (diff === -1) return "d";
  } else {
    if (diff === 0) return "M";
    if (diff === -1) return "m";
    if (diff === 1) return "A";
    if (diff === -2) return "d";
  }
  throw new Error(`Cannot classify interval: ${iv.number} with ${iv.semitones} semitones`);
}

/** Compute the interval between two pitches (lower → higher). */
export function intervalBetween(a: Pitch, b: Pitch): Interval {
  const semitones = toMidi(b) - toMidi(a);
  // Letter distance: count diatonic steps from a to b, accounting for octaves.
  const letterDist = (letterIndex(b.letter) - letterIndex(a.letter)) + (b.octave - a.octave) * 7;
  // Interval number is letter distance + 1 (a unison = 0 letter steps).
  const number = letterDist + 1;
  return { number, semitones };
}

/** Render as "M3", "P5", "m7", "A4", "d5". */
export function intervalToString(iv: Interval): string {
  return `${intervalQuality(iv)}${iv.number}`;
}

/**
 * Parse interval shorthand: "M3", "P5", "m7", "A4", "d5", "M9", "P11", "M13".
 * Returns null on malformed input.
 */
export function parseInterval(s: string): Interval | null {
  const match = s.match(/^(P|M|m|A|d)(\d+)$/);
  if (!match) return null;
  const quality = match[1] as IntervalQuality;
  const number = parseInt(match[2]!, 10);
  try {
    return interval(quality, number);
  } catch {
    return null;
  }
}

/** Transpose a pitch UP by an interval. Use a negative-semitone interval for down. */
export function transposeByInterval(p: Pitch, iv: Interval): Pitch {
  const letterSteps = iv.number - 1;
  return transposeByLetterAndSemitones(p, letterSteps, iv.semitones);
}

/** Transpose DOWN by an interval. */
export function transposeDownByInterval(p: Pitch, iv: Interval): Pitch {
  return transposeByLetterAndSemitones(p, -(iv.number - 1), -iv.semitones);
}

/** Invert an interval — what's left when you flip it within an octave. M3 → m6, P5 → P4. */
export function invertInterval(iv: Interval): Interval {
  const simple = ((iv.number - 1) % 7) + 1;
  if (simple === 1 || simple === 8) {
    // unison or octave inverts to itself in number
    const newNumber = simple === 1 ? 8 : 1;
    const newSemis = 12 - (iv.semitones % 12);
    return { number: newNumber, semitones: newSemis === 12 ? 0 : newSemis };
  }
  const newNumber = 9 - simple;
  const newSemis = 12 - ((iv.semitones % 12 + 12) % 12);
  return { number: newNumber, semitones: newSemis };
}

/**
 * Consonance classification by common-practice rules.
 * - perfect consonant: P1, P5, P8 (and in modern usage P4 ambiguously)
 * - imperfect consonant: M3, m3, M6, m6
 * - dissonant: M2, m2, M7, m7, all augmented/diminished, P4 in some contexts
 *
 * P4 is classified as "perfect-consonant" here for the modern listener; classical
 * theory often calls it dissonant against the bass. Document this choice in UI.
 */
export type ConsonanceClass = "perfect-consonant" | "imperfect-consonant" | "dissonant";

export function consonance(iv: Interval): ConsonanceClass {
  const quality = intervalQuality(iv);
  const simple = ((iv.number - 1) % 7) + 1;
  if (quality === "P" && [1, 4, 5, 8].includes(simple)) return "perfect-consonant";
  if ((quality === "M" || quality === "m") && [3, 6].includes(simple)) return "imperfect-consonant";
  return "dissonant";
}
